import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function MortgageCalculator() {
  const [loanAmount, setLoanAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [loanTerm, setLoanTerm] = useState("");
  const [result, setResult] = useState<{ monthlyPayment: number; totalPayment: number; totalInterest: number } | null>(null);

  const calculate = () => {
    const P = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate);
    const years = parseFloat(loanTerm);

    if (isNaN(P) || isNaN(annualRate) || isNaN(years) || P <= 0 || annualRate <= 0 || years <= 0) {
      toast.error("Please enter valid positive values");
      return;
    }

    const monthlyRate = annualRate / 100 / 12;
    const numPayments = years * 12;
    const monthlyPayment = P * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    const totalPayment = monthlyPayment * numPayments;
    const totalInterest = totalPayment - P;

    setResult({ monthlyPayment, totalPayment, totalInterest });
    toast.success("Mortgage calculated!");
  };

  const reset = () => {
    setLoanAmount("");
    setInterestRate("");
    setLoanTerm("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Mortgage Calculator" 
      calculatorPath="/calculator/mortgage"
      category="finance"
      subcategory="Loans"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Mortgage Calculator</h1>
          <p className="text-muted-foreground">
            Calculate monthly mortgage payments, total interest, and amortization
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: M = P[r(1+r)^n] / [(1+r)^n-1]</CardTitle>
            <CardDescription>
              Where M is monthly payment, P is principal, r is monthly rate, n is number of payments
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="loanAmount">Loan Amount ($)</Label>
                <Input
                  id="loanAmount"
                  type="number"
                  placeholder="300000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="interestRate">Annual Interest Rate (%)</Label>
                <Input
                  id="interestRate"
                  type="number"
                  placeholder="4.5"
                  value={interestRate}
                  onChange={(e) => setInterestRate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loanTerm">Loan Term (years)</Label>
                <Input
                  id="loanTerm"
                  type="number"
                  placeholder="30"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculate} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">Reset</Button>
            </div>

            {result && (
              <>
                <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 border-emerald-500/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Results</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-lg break-all overflow-wrap-anywhere">
                        <strong>Monthly Payment:</strong> ${result.monthlyPayment.toFixed(2)}
                      </p>
                      <CopyResultButton value={result.monthlyPayment.toFixed(2)} />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-lg break-all overflow-wrap-anywhere">
                        <strong>Total Payment:</strong> ${result.totalPayment.toFixed(2)}
                      </p>
                      <CopyResultButton value={result.totalPayment.toFixed(2)} />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-lg break-all overflow-wrap-anywhere">
                        <strong>Total Interest:</strong> ${result.totalInterest.toFixed(2)}
                      </p>
                      <CopyResultButton value={result.totalInterest.toFixed(2)} />
                    </div>
                  </CardContent>
                </Card>

                <CalculatorSuperCalcCTA config={calculatorCTAs["mortgage"]} trigger={result.monthlyPayment} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the home loan amount</p>
                <p>2. Enter the annual interest rate (APR)</p>
                <p>3. Enter the loan term in years (typically 15 or 30)</p>
                <p>4. Click Calculate to see monthly payment and total costs</p>
              </CardContent>
            </Card>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Example</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p><strong>Problem:</strong> $300,000 mortgage at 4.5% APR for 30 years.</p>
                <p><strong>Solution:</strong></p>
                <div className="pl-4 space-y-1">
                  <p>Monthly rate = 4.5% / 12 = 0.375%</p>
                  <p>Number of payments = 30 × 12 = 360</p>
                  <p>Monthly payment ≈ $1,520.06</p>
                  <p>Total paid = $1,520.06 × 360 = $547,220.16</p>
                  <p>Total interest = $547,220.16 - $300,000 = $247,220.16</p>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
